import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import * as React from 'react'
import { useCallback } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import IconButton from 'LuxKit/Button/IconButton'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DAY_LONG_MONTH, INTL_DAY_LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import OptionsButton from 'tripPlanner/components/Documents/OptionsButton'
import { OptionsMenuActions, OptionsMenuButton } from 'tripPlanner/components/Documents/OptionsMenuActions'
import RecentItemInlineTitleEdit from 'tripPlanner/pages/chat/RecentItemInlineTitleEdit'
import NestableClickable from 'components/Common/NestableClickable'
import { getCompactRecentChatTimestampLabel } from 'tripPlanner/pages/chat/DrawerRecentChatListItem'
import useBeginRecentItemRename from 'tripPlanner/assistant/components/useBeginRecentItemRename'
import useOpenRecentChatItemActionsSheet from 'tripPlanner/assistant/components/useOpenRecentChatItemActionsSheet'
import type { RecentChatItemActionsSheetItemType } from 'tripPlanner/assistant/components/RecentChatItemActionsSheet'

const NOOP_MOUSE_EVENT = {
  preventDefault: () => undefined,
  stopPropagation: () => undefined,
} as React.MouseEvent

const Container = styled(NestableClickable)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  min-height: ${rem(112)};
  padding: ${rem(16)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  text-align: start;
  outline: none;
  cursor: pointer;
  box-shadow: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  ${mediaHoverable} {
    &:hover {
      box-shadow: ${props => props.theme.shadow.bottom.small};
    }
  }

  &:focus-visible {
    border-color: ${props => props.theme.palette.brand.primary.normal};
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }

  ${mediaQueryDown.mobile} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: ${rem(8)};
    row-gap: ${rem(4)};
    align-items: start;
    min-height: 0;
    padding-block: ${rem(14)};
    padding-inline: ${rem(16)};
    background: ${props => props.theme.palette.neutral.surface.normalBackground};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-radius: ${props => props.theme.borderRadius.L};
    box-shadow: none;

    &:focus-visible {
      border-color: ${props => props.theme.palette.brand.primary.normal};
      box-shadow: none;
    }
  }
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(8)};
  width: 100%;

  ${mediaQueryDown.mobile} {
    display: contents;
  }
`

const TitleContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;

  ${mediaQueryDown.mobile} {
    grid-column: 1;
    grid-row: 1;
    flex: initial;
  }
`

const TitleLine = styled.div`
  display: block;
  min-width: 0;

  ${mediaQueryDown.mobile} {
    display: flex;
    align-items: baseline;
    gap: ${rem(8)};
    width: 100%;
  }
`

const TitleLineText = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`

const ActionsColumn = styled.div`
  flex-shrink: 0;

  ${mediaQueryDown.mobile} {
    grid-column: 2;
    grid-row: 1;
    align-self: start;
  }
`

const Description = styled(BodyText)`
  margin-block-start: ${rem(6)};
`

const LastMessage = styled(BodyText)`
  margin-block-start: ${rem(6)};
`

const MobilePreview = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: baseline;
  column-gap: ${rem(4)};
  width: 100%;
  min-width: 0;

  &.with-separator {
    grid-template-columns: auto auto minmax(0, 1fr);
  }
`

const MobileTimestamp = styled(Caption)`
  flex-shrink: 0;
`

const Separator = styled.span`
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  font-family: ${props => props.theme.font.primary.family};
  /* Absolute px to match Caption smallalt (not rem-scaled on mobile). */
  font-size: 11px;
  line-height: 14px;

  &::before {
    content: '·';
  }
`

const PreviewText = styled(BodyText)`
  min-width: 0;
`

const Timestamp = styled(BodyText)`
  margin-block-start: auto;
  padding-block-start: ${rem(10)};
`

interface Props {
  itemType: RecentChatItemActionsSheetItemType
  title?: string
  subtitle?: string
  description?: string
  updatedAt: string
  to: string
  onClick?: React.MouseEventHandler
  onRename: (name: string) => Promise<void>
  onDeleteClick: (closeMenu: () => void) => (event: React.MouseEvent) => void
}

export function getRecentChatTimestampLabel(
  updatedAt: string,
  formatMessage: IntlShape['formatMessage'],
  now = moment(),
): string {
  const updatedAtMoment = moment(updatedAt)
  if (!updatedAtMoment.isValid()) {
    return intlDateFormatter(updatedAt, INTL_DAY_LONG_MONTH)
  }

  const dateFormat = updatedAtMoment.year() === now.year() ? INTL_DAY_LONG_MONTH : INTL_DAY_LONG_MONTH_LONG_YEAR
  if (updatedAtMoment.isAfter(now)) {
    return intlDateFormatter(updatedAt, dateFormat)
  }

  const elapsed = moment.duration(now.diff(updatedAtMoment))
  const elapsedMinutes = elapsed.asMinutes()
  const elapsedHours = elapsed.asHours()
  const daysAgo = now.diff(updatedAtMoment, 'days')

  if (elapsedMinutes < 60) {
    const minutesAgo = Math.max(1, Math.floor(elapsedMinutes))

    return formatMessage({
      id: 'WsgRYtkbjuV_vQsqimvA',
      defaultMessage: '{count, plural, one {# minute ago} other {# minutes ago}}',
      description: 'Recent chat timestamp shown when the chat was updated less than one day ago',
    }, { count: minutesAgo })
  }

  if (elapsedHours < 24) {
    const hoursAgo = Math.max(1, Math.min(23, Math.round(elapsedHours)))

    return formatMessage({
      id: 'eoO6OW120iob3ywV6CWK',
      defaultMessage: '{count, plural, one {# hour ago} other {# hours ago}}',
      description: 'Recent chat timestamp shown when the chat was updated less than one day ago but at least one hour ago',
    }, { count: hoursAgo })
  }

  if (daysAgo <= 7) {
    return formatMessage({
      id: 'PHT7fqRxB3FBB7NfmiST',
      defaultMessage: '{count, plural, one {# day ago} other {# days ago}}',
      description: 'Recent chat timestamp shown when the chat was updated within the last seven days',
    }, { count: daysAgo })
  }

  return intlDateFormatter(updatedAt, dateFormat)
}

export default function ChatLandingRecentChatTile({
  itemType,
  title,
  subtitle,
  description,
  updatedAt,
  to,
  onClick,
  onRename,
  onDeleteClick,
}: Props) {
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const untitledLabel = intl.formatMessage({
    id: 'P7xyHLCCjSlOklqXsVYg',
    defaultMessage: 'Untitled',
    description: 'Default title for untitled trip in the trip planner feature',
  })
  const {
    isRenaming,
    setIsRenaming,
    titleEditRef,
    beginRename,
    displayTitle,
    handleRenamed,
  } = useBeginRecentItemRename(title, untitledLabel)
  const formattedDate = isMobile
    ? getCompactRecentChatTimestampLabel(updatedAt, intl)
    : getRecentChatTimestampLabel(updatedAt, intl.formatMessage)
  const actionsTitle = intl.formatMessage({
    id: 'pi4uwekopu4hL5smnBsK',
    defaultMessage: 'Recent chat actions',
    description: 'Accessible title for the recent chat actions dropdown menu on the trip planner landing page',
  })
  const actionMenuAriaLabel = intl.formatMessage({
    id: 'OSwxcyWZf8-szg3KOL5Q',
    defaultMessage: 'action menu',
    description: 'Accessible label for the document options menu button',
  })
  const deleteText = intl.formatMessage({
    id: 'cWmw1tD3F1HR1j-s27sR',
    defaultMessage: 'Delete',
    description: 'Button label to delete a recent chat or trip from the trip planner landing page',
  })
  const renameText = intl.formatMessage({
    id: '04s1xvbNcf0Iw3ACds_n',
    defaultMessage: 'Rename',
    description: 'Button label to rename a recent chat or trip from the trip planner landing page',
  })

  const handleRename = useCallback(async(name: string) => {
    await onRename(name)
    handleRenamed(name)
  }, [handleRenamed, onRename])

  const handleSheetDelete = useCallback(() => {
    onDeleteClick(() => undefined)(NOOP_MOUSE_EVENT)
  }, [onDeleteClick])

  const openActionsSheet = useOpenRecentChatItemActionsSheet({
    itemType,
    title: displayTitle,
    onRename: handleRename,
    onDelete: handleSheetDelete,
  })

  const handleRenameClick = (closeMenu: () => void) => (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    beginRename(closeMenu)
  }

  const isDesktopRenaming = !isMobile && isRenaming
  const mobilePreviewClassName = formattedDate && subtitle ? 'with-separator' : undefined

  return (
    <Container
      to={isDesktopRenaming ? undefined : to}
      onClick={isDesktopRenaming ? undefined : onClick}
    >
      <Header>
        <TitleContent>
          <TitleLine>
            <TitleLineText>
              <RecentItemInlineTitleEdit
                ref={titleEditRef}
                title={displayTitle}
                fallbackTitle={untitledLabel}
                isEditing={isDesktopRenaming}
                variant="small"
                onEditingChange={setIsRenaming}
                onRename={handleRename}
              />
            </TitleLineText>
          </TitleLine>
          {!!description && !isMobile && (
            <Description variant="small" weight="normal" align="start" colour="normal-contrast" lineClamp={2}>
              {description}
            </Description>
          )}
          {!!subtitle && !isMobile && (
            <LastMessage variant="small" weight="normal" italics align="start" colour="light-foreground-alt" lineClamp={1}>
              {subtitle}
            </LastMessage>
          )}
          {isMobile && (!!formattedDate || !!subtitle) && (
            <MobilePreview className={mobilePreviewClassName}>
              {!!formattedDate && (
                <MobileTimestamp variant="smallalt" weight="normal" colour="light-foreground-alt">
                  {formattedDate}
                </MobileTimestamp>
              )}
              {!!formattedDate && !!subtitle && (
                <Separator aria-hidden />
              )}
              {!!subtitle && (
                <PreviewText variant="small" colour="light-foreground-alt" italics lineClamp={1}>
                  {subtitle}
                </PreviewText>
              )}
            </MobilePreview>
          )}
        </TitleContent>
        <ActionsColumn>
          {isMobile ? (
            <IconButton
              kind="tertiary"
              shape="circle"
              size="small"
              data-testid="action-menu-button"
              aria-label={actionMenuAriaLabel}
              title={actionsTitle}
              onClick={openActionsSheet}
            >
              <LineEllipsisVIcon />
            </IconButton>
          ) : (
            <OptionsButton
              title={actionsTitle}
              bodyContent={(closeMenu) => (
                <OptionsMenuActions>
                  <OptionsMenuButton type="button" onClick={handleRenameClick(closeMenu)}>
                    {renameText}
                  </OptionsMenuButton>
                  <OptionsMenuButton type="button" onClick={onDeleteClick(closeMenu)}>
                    {deleteText}
                  </OptionsMenuButton>
                </OptionsMenuActions>
              )}
            />
          )}
        </ActionsColumn>
      </Header>
      {!isMobile && (
        <Timestamp variant="small" weight="normal" align="start" colour="normal-contrast" lineClamp={1}>
          {formattedDate}
        </Timestamp>
      )}
    </Container>
  )
}
